import { Button } from 'designSystem/components/Button'
import { Toggle } from 'designSystem/components/Toggle'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'designSystem/components/Tooltip'
import { AGENTIC_SEARCH_FEEDBACK, useGeckoAnalytics } from 'features/analytics'
import { useAdvancedSearchUX } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/AdvancedSearchUXContext'
import { DocumentsBugReportButton } from 'features/knowledgeDocumentViewer/components/bugReport/DocumentsBugReportButton'
import type { ViewerBugReportContext } from 'features/knowledgeDocumentViewer/utils/viewerBugReport'
import { useGeckoPermissions } from 'features/permissions/hooks/useGeckoPerms'
import {
  CircleCheck,
  Copy,
  RotateCcw,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: -0.25rem;
  color: var(--muted-foreground);
`

const CopiedCheckIcon = styled(CircleCheck)`
  color: ${(props) => props.theme.colors.success['400_base']};
`

const COPIED_RESET_MS = 2000

type AgenticMessageActionsProps = {
  copyText: string
  retryQuery: string
}

export function AgenticMessageActions({
  copyText,
  retryQuery,
}: AgenticMessageActionsProps): React.ReactElement {
  const { siteId, onSuggestionSelect } = useAdvancedSearchUX()
  const { trackEvent } = useGeckoAnalytics()
  const { user } = useGeckoPermissions()
  const [rating, setRating] = useState<'up' | 'down' | null>(null)
  const [copied, setCopied] = useState(false)
  const copiedResetTimeoutRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (copiedResetTimeoutRef.current !== null) {
        window.clearTimeout(copiedResetTimeoutRef.current)
      }
    }
  }, [])

  const buildBugReportContext = useCallback(
    (action: string): ViewerBugReportContext => ({
      reportedByEmail: user?.email,
      siteId,
      searchQuery: retryQuery,
      recentActions: [action],
    }),
    [retryQuery, siteId, user?.email]
  )

  const handleCopy = useCallback(async () => {
    const text = copyText.trim()
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      if (copiedResetTimeoutRef.current !== null) {
        window.clearTimeout(copiedResetTimeoutRef.current)
      }
      copiedResetTimeoutRef.current = window.setTimeout(() => {
        setCopied(false)
        copiedResetTimeoutRef.current = null
      }, COPIED_RESET_MS)
    } catch {
      setCopied(false)
    }
  }, [copyText])

  const handleRetry = useCallback(() => {
    const trimmed = retryQuery.trim()
    if (!trimmed) return
    onSuggestionSelect(trimmed, {
      clearInputAfterSubmit: true,
      startAgenticTurn: true,
    })
  }, [onSuggestionSelect, retryQuery])

  const handleHelpful = useCallback(() => {
    if (rating === 'up') {
      setRating(null)
      return
    }
    setRating('up')
    trackEvent(AGENTIC_SEARCH_FEEDBACK.title, {
      rating: 'up',
      site_id: siteId,
      search_query: retryQuery,
    })
  }, [rating, retryQuery, siteId, trackEvent])

  const canCopy = copyText.trim().length > 0
  const canRetry = retryQuery.trim().length > 0

  return (
    <ActionsRow>
      <Tooltip open={copied ? true : undefined}>
        <TooltipTrigger asChild>
          <Button
            type='button'
            variant='ghost'
            size='icon'
            aria-label={copied ? 'Copied' : 'Copy response'}
            disabled={!canCopy}
            onClick={handleCopy}>
            {copied ? <CopiedCheckIcon /> : <Copy />}
          </Button>
        </TooltipTrigger>
        <TooltipContent side='top'>{copied ? 'Copied' : 'Copy'}</TooltipContent>
      </Tooltip>
      <Toggle
        aria-label='Helpful'
        title='Helpful'
        pressed={rating === 'up'}
        onPressedChange={handleHelpful}>
        <ThumbsUp size={14} fill={rating === 'up' ? 'currentColor' : 'none'} />
      </Toggle>
      <DocumentsBugReportButton
        buildContext={buildBugReportContext}
        openActionLabel='Opened bug report from agentic search feedback'
        onSubmitSuccess={() => {
          setRating('down')
          trackEvent(AGENTIC_SEARCH_FEEDBACK.title, {
            rating: 'down',
            site_id: siteId,
            search_query: retryQuery,
          })
        }}
        renderTrigger={({ disabled, onClick }) => (
          <Toggle
            aria-label='Report issue'
            title='Report issue'
            pressed={rating === 'down'}
            disabled={disabled}
            onPressedChange={(pressed) => {
              if (pressed) {
                onClick()
              } else {
                setRating(null)
              }
            }}>
            <ThumbsDown
              size={14}
              fill={rating === 'down' ? 'currentColor' : 'none'}
            />
          </Toggle>
        )}
      />
      <Button
        type='button'
        variant='ghost'
        size='icon'
        aria-label='Retry search'
        title='Retry'
        disabled={!canRetry}
        onClick={handleRetry}>
        <RotateCcw />
      </Button>
    </ActionsRow>
  )
}
